import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, MessageCircle, Save, Unlock, Wallet } from "lucide-react";
import { api, dt, errMsg, rp, waLink } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const Card = ({ title, children, testId }) => (
  <div className="rounded-2xl border border-border bg-card p-6" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{title}</p>
    <div className="mt-4">{children}</div>
  </div>
);

export default function CustomerCard() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/ftth/customers/${id}/detail`);
      setD(data);
      setNotes(data.customer.notes || "");
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveNotes = async () => {
    setBusy(true);
    try { await api.put(`/ftth/customers/${id}/notes`, { notes }); toast.success("Catatan disimpan"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const act = async (path, msg) => {
    try { await api.post(`/ftth/customers/${id}/${path}`, {}); toast.success(msg); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!d) return <p className="text-sm text-muted-foreground">Memuat kartu pelanggan…</p>;
  const c = d.customer;

  return (
    <div className="fade-up space-y-6" data-testid="customer-card-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Button asChild variant="ghost" size="sm" className="rounded-full"><Link to="/pelanggan"><ArrowLeft className="mr-1 h-4 w-4" /> Daftar pelanggan</Link></Button>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="cust-title">{c.name}</h1>
          <p className="font-mono text-xs text-muted-foreground">{c.secret_name} · {c.profile || "-"} · tagihan {rp(c.price)} · jatuh tempo tgl {c.due_day}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="rounded-full bg-emerald-600 text-white" onClick={() => act("pay", "Pembayaran dicatat")} data-testid="card-pay"><Wallet className="mr-1 h-3.5 w-3.5" /> Bayar</Button>
          {c.status === "isolir" ? (
            <Button size="sm" variant="outline" className="rounded-full text-emerald-400" onClick={() => act("restore", "Isolir dibuka")} data-testid="card-restore"><Unlock className="mr-1 h-3.5 w-3.5" /> Buka Isolir</Button>
          ) : (
            <Button size="sm" variant="outline" className="rounded-full text-amber-400" onClick={() => act("isolir", "Pelanggan diisolir")} data-testid="card-isolir"><Lock className="mr-1 h-3.5 w-3.5" /> Isolir</Button>
          )}
          {c.phone ? (
            <Button asChild size="sm" variant="outline" className="rounded-full">
              <a href={waLink(c.phone, `Halo ${c.name}, informasi layanan internet Anda.`)} target="_blank" rel="noreferrer" data-testid="card-wa"><MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp</a>
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card title="Status" testId="card-status">
          <p className="font-mono text-xl">{c.status}</p>
          {c.grace_until ? <p className="mt-1 text-xs text-cyan-400">tunda s/d {c.grace_until}</p> : null}
        </Card>
        <Card title="Total Dibayar" testId="card-total-paid"><p className="font-mono text-xl">{rp(d.total_paid)}</p></Card>
        <Card title="Tunggakan" testId="card-outstanding">
          <p className="font-mono text-xl text-red-400">{rp(d.outstanding)}</p>
          <p className="mt-1 text-xs text-muted-foreground">{d.unpaid_periods.length} bulan belum lunas</p>
        </Card>
        <Card title="Kontak & Alamat" testId="card-contact">
          <p className="font-mono text-sm">{c.phone || "-"}</p>
          <p className="mt-1 text-xs text-muted-foreground">{c.address || "alamat belum diisi"}</p>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Riwayat Tagihan" testId="card-bills">
          <div className="max-h-[300px] space-y-2 overflow-y-auto scrollbar-thin">
            {d.bills.map((b) => (
              <div key={b.period} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 text-xs">
                <div><p className="font-mono">{b.period} · {rp(b.amount)}</p>{b.paid ? <p className="font-mono text-[10px] text-muted-foreground">{b.invoice}</p> : null}</div>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] ${b.paid ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>{b.paid ? "lunas" : "belum lunas"}</span>
              </div>
            ))}
            {!d.bills.length && <p className="text-sm text-muted-foreground">Belum ada tagihan.</p>}
          </div>
        </Card>

        <Card title="Riwayat Isolir" testId="card-isolir-logs">
          <div className="max-h-[300px] space-y-2 overflow-y-auto scrollbar-thin">
            {d.isolir_logs.map((l) => (
              <div key={l.id} className="rounded-xl border border-border px-3 py-2 text-xs">
                <p className="font-mono">{l.action === "isolir" ? <span className="text-red-400">isolir</span> : <span className="text-emerald-400">buka</span>} · oleh {l.by}</p>
                <p className="text-muted-foreground">{l.reason}</p>
                <p className="font-mono text-[10px] text-muted-foreground">{dt(l.created_at)}</p>
              </div>
            ))}
            {!d.isolir_logs.length && <p className="text-sm text-muted-foreground">Belum pernah diisolir.</p>}
          </div>
        </Card>

        <Card title="Riwayat Pembayaran" testId="card-payments">
          <div className="max-h-[260px] space-y-2 overflow-y-auto scrollbar-thin">
            {d.payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-xl border border-border px-3 py-2 font-mono text-xs">
                <span>{p.invoice} · {p.period}</span><span className="text-emerald-400">{rp(p.amount)}</span>
              </div>
            ))}
            {!d.payments.length && <p className="text-sm text-muted-foreground">Belum ada pembayaran.</p>}
          </div>
        </Card>

        <Card title="Catatan Pelanggan" testId="card-notes">
          <Textarea
            rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="notes-input"
            placeholder="Catatan teknis, riwayat kunjungan, kesepakatan pembayaran…"
            className="rounded-xl bg-secondary/50"
          />
          <Button onClick={saveNotes} disabled={busy} data-testid="notes-save" className="mt-3 rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white">
            <Save className="mr-1 h-4 w-4" /> Simpan Catatan
          </Button>
          {d.notifications.length ? (
            <div className="mt-5 space-y-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Notifikasi terkait</p>
              {d.notifications.slice(0, 5).map((n) => (
                <p key={n.id} className="rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground">{n.message} <span className="font-mono text-[10px]">{dt(n.created_at)}</span></p>
              ))}
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
